import {
	useLayoutEffect,
	useState,
	type CSSProperties,
	type RefObject,
} from "react";

const DEFAULT_PLACEHOLDER_POSITION = {
	left: 12,
	top: 12,
};
const PLACEHOLDER_LEFT_OFFSET = 4;

type TPlaceholderStyle = CSSProperties & {
	"--code-mirror-placeholder-left": string;
	"--code-mirror-placeholder-top": string;
};

export const useCodeMirrorPlaceholderPosition = (
	shellRef: RefObject<HTMLDivElement | null>,
	enabled: boolean
): CSSProperties | undefined => {
	const [position, setPosition] = useState(
		DEFAULT_PLACEHOLDER_POSITION
	);

	useLayoutEffect(() => {
		if (!enabled) {
			return;
		}

		const shell = shellRef.current;
		if (!shell) {
			return;
		}

		const updatePosition = () => {
			const target =
				shell.querySelector<HTMLElement>(".cm-line") ??
				shell.querySelector<HTMLElement>(".cm-content");

			if (!target) {
				return;
			}

			const shellRect = shell.getBoundingClientRect();
			const targetRect = target.getBoundingClientRect();
			const nextPosition = {
				left: Math.max(
					targetRect.left -
						shellRect.left +
						PLACEHOLDER_LEFT_OFFSET,
					0
				),
				top: Math.max(targetRect.top - shellRect.top, 0),
			};

			setPosition((previousPosition) => {
				if (
					previousPosition.left === nextPosition.left &&
					previousPosition.top === nextPosition.top
				) {
					return previousPosition;
				}

				return nextPosition;
			});
		};

		const frameId = window.requestAnimationFrame(
			updatePosition
		);
		const resizeObserver =
			typeof ResizeObserver !== "undefined"
				? new ResizeObserver(() => {
						updatePosition();
					})
				: null;

		resizeObserver?.observe(shell);
		const scroller = shell.querySelector(".cm-scroller");
		if (scroller instanceof HTMLElement) {
			resizeObserver?.observe(scroller);
		}

		return () => {
			window.cancelAnimationFrame(frameId);
			resizeObserver?.disconnect();
		};
	}, [enabled, shellRef]);

	if (!enabled) {
		return undefined;
	}

	const placeholderStyle: TPlaceholderStyle = {
		"--code-mirror-placeholder-left": `${position.left}px`,
		"--code-mirror-placeholder-top": `${position.top}px`,
	};

	return placeholderStyle;
};
